<template>
  <div class="calendar_wrapper">
    <div class="calendar">
      <!-- < left arrow | month/year | right arrow > -->
      <div class="calendar_header">
        <a
          class="calendar_arrow"
          v-if="showYearArrows"
          @click="movePreviousYear"
          ><i class="fas fa-angle-double-left"></i
        ></a>
        <a class="calendar_arrow" @click="movePreviousMonth"
          ><i class="fas fa-angle-left"></i
        ></a>
        <span class="calendar_title" @click="moveThisMonth">{{
          header.label
        }}</span>
        <a class="calendar_arrow" @click="moveNextMonth"
          ><i class="fas fa-angle-right"></i
        ></a>
        <a class="calendar_arrow" v-if="showYearArrows" @click="moveNextYear"
          ><i class="fas fa-angle-double-right"></i
        ></a>
      </div>
      <!-- Sun thru Sat headings -->
      <div class="calendar_weekdays">
        <div
          class="calendar_weekday"
          v-for="(weekday, idx) in weekdays"
          :key="idx"
        >
          {{ weekday.label_3 }}
        </div>
      </div>
      <!-- weeks in the month -->
      <div class="calendar_week" v-for="(week, idx) in weeks" :key="idx">
        <!-- clickable day -->
        <div
          v-for="(day, idx) in week"
          :key="idx"
          :day="day.day"
          :month="day.month"
          :year="day.year"
          class="calendar_day"
          :class="{
            calendar_today: day.isToday && day.label !== '',
            'calendar_not-in-month': !day.inMonth,
            is_selected: day.isSelected,
            is_active_monday: day.isMonday,
          }"
          @click="clickDay(day)"
        >
          {{ day[dayKey] }}
        </div>
      </div>
    </div>
  </div>
</template>
<script>
const _today = new Date()
const _todayComps = {
  year: _today.getFullYear(),
  month: _today.getMonth() + 1,
  day: _today.getDate(),
}
export default {
  name: 'z-calendar',
  props: {
    config: {
      type: Object,
      default: null,
    },
    modelValue: [Number, String, Date],
    dayKey: { type: String, default: 'label' },
  },
  data() {
    return {
      month: _todayComps.month,
      year: _todayComps.year,
      today: new Date(),
      daysInMonths: [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31],
      monday: null,
      sunday: null,
      selectedDay: null,
      selectedWeek: null,
      clickableWeeks: null,
    }
  },
  emits: ['update:modelValue', 'selectedDay', 'selectedWeek', 'disableWeek'],
  mounted() {
    this.getWeek(this.today)
  },
  computed: {
    monthIndex() {
      return this.month - 1
    },
    todayComps() {
      return {
        year: this.today.getFullYear(),
        month: this.today.getMonth() + 1,
        day: this.today.getDate(),
      }
    },
    isLeapYear() {
      return (
        (this.year % 4 === 0 && this.year % 100 !== 0) || this.year % 400 === 0
      )
    },
    previousMonthComps() {
      if (this.month === 1) {
        return {
          days: this.daysInMonths[11],
          month: 12,
          year: this.year - 1,
        }
      }
      return {
        days:
          this.month === 3 && this.isLeapYear
            ? 29
            : this.daysInMonths[this.month - 2],
        month: this.month - 1,
        year: this.year,
      }
    },
    nextMonthComps() {
      if (this.month === 12) {
        return {
          days: this.daysInMonths[0],
          month: 1,
          year: this.year + 1,
        }
      }
      return {
        days:
          this.month === 2 && this.isLeapYear
            ? 29
            : this.daysInMonths[this.month],
        month: this.month + 1,
        year: this.year,
      }
    },
    showYearArrows() {
      let _show = true
      if (this.config && typeof this.config.showYearArrows !== 'undefined') {
        _show = this.config.showYearArrows
      }
      return _show
    },
    months() {
      const months =
        this.config &&
        typeof this.config.months !== 'undefined' &&
        this.config.months.length === 12
          ? this.config.months
          : [
              'January',
              'February',
              'March',
              'April',
              'May',
              'June',
              'July',
              'August',
              'September',
              'October',
              'November',
              'December',
            ]
      return months.map((ml, i) => ({
        label: ml,
        label_1: ml.substring(0, 1),
        label_2: ml.substring(0, 2),
        label_3: ml.substring(0, 3),
        number: i + 1,
      }))
    },
    weekdays() {
      const weekDays = [
        ...(this.config &&
        typeof this.config.weekDays !== 'undefined' &&
        this.config.weekDays.length === 7
          ? this.config.weekDays
          : [
              'Sunday',
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday',
              'Saturday',
            ]),
      ]
      for (let i = 1; i <= 8 - this.firstDayOfWeek; i++) {
        weekDays.push(weekDays.shift())
      }
      return weekDays.map((wl, i) => ({
        label: wl,
        label_1: wl.substring(0, 1),
        label_2: wl.substring(0, 2),
        label_3: wl.substring(0, 3),
        number: i + 1,
      }))
    },
    header() {
      const month = this.months[this.monthIndex]
      return {
        month: month,
        year: this.year.toString(),
        shortYear: this.year.toString().substring(2, 4),
        label: month.label + ' ' + this.year,
      }
    },
    firstDayOfWeek() {
      return this.config && typeof this.config.firstDayOfWeek !== 'undefined'
        ? this.config.firstDayOfWeek
        : 1
    },
    firstWeekdayInMonth() {
      let r =
        new Date(this.year, this.monthIndex, 1).getDay() + this.firstDayOfWeek
      if (r > 7) {
        r = r - 7
      }
      return r
    },
    daysInMonth() {
      if (this.month === 2 && this.isLeapYear) return 29
      return this.daysInMonths[this.monthIndex]
    },
    weeks() {
      let cnt = 0
      const weeks = []
      let previousMonth = true
      let thisMonth = false
      let nextMonth = false
      let day = this.previousMonthComps.days - this.firstWeekdayInMonth + 2
      let month = this.previousMonthComps.month
      let year = this.previousMonthComps.year
      for (let w = 1; w <= 6 && !nextMonth; w++) {
        const week = []
        for (let d = 1; d <= 7; d++) {
          if (previousMonth && d >= this.firstWeekdayInMonth) {
            day = 1
            month = this.month
            year = this.year
            previousMonth = false
            thisMonth = true
          }
          let dayDate = new Date(year, month - 1, day)
          const dayInfo = {
            label: day && thisMonth ? day.toString() : '',
            day,
            weekday: d,
            week: w,
            month,
            year,
            date: dayDate,
            beforeMonth: previousMonth,
            afterMonth: nextMonth,
            inMonth: thisMonth,
            isToday:
              day === this.todayComps.day &&
              month === this.todayComps.month &&
              year === this.todayComps.year,
            isFirstDay: thisMonth && day === 1,
            isLastDay: thisMonth && day === this.daysInMonth,
            isSelected: false,
            isMonday: false,
          }
          this.configureDay(dayInfo)
          week.push(dayInfo)
          if (thisMonth && day >= this.daysInMonth) {
            thisMonth = false
            nextMonth = true
            day = 1
            month = this.nextMonthComps.month
            year = this.nextMonthComps.year
          } else {
            day++
          }
        }
        weeks.push(week)
      }
      return weeks
    },
    // editableMondays() {
    //   let now = new Date()
    //   let mondays = []
    //   let evalDate = null
    //   let numWeeks = 0
    //   let thisMonday = this.getMonday(now)
    //   if (this.config.type === 'multiple') {
    //     evalDate = new Date(
    //       thisMonday.getFullYear(),
    //       thisMonday.getMonth(),
    //       thisMonday.getDate() - 0
    //     )
    //     numWeeks = 3
    //   } else {
    //     evalDate = new Date(
    //       thisMonday.getFullYear(),
    //       thisMonday.getMonth(),
    //       thisMonday.getDate() - 14
    //     )
    //     numWeeks = 5
    //   }
    //   evalDate.setHours(0, 0, 0, 0)
    //   for (var i = 0; i < numWeeks; i++) {
    //     mondays.push(evalDate)
    //     evalDate = new Date(
    //       evalDate.getFullYear(),
    //       evalDate.getMonth(),
    //       evalDate.getDate() + 7
    //     )
    //     evalDate.setHours(0, 0, 0, 0)
    //   }
    //   return mondays
    // },
  },
  methods: {
    configureDay(day) {
      // determine if it's a Monday, then determine if within past/forward two weeks
      // to set as an active Monday
      let dayOfWeek = day.date.getDay()
      if (dayOfWeek === 1) {
        if (day.label !== '') {
          day.isMonday = true
        }
        // let thisMonday = this.getMonday(this.today)
        // let firstMonday = new Date(thisMonday)
        // firstMonday.setDate(firstMonday.getDate() - 14)
        // let lastMonday = new Date(thisMonday)
        // lastMonday.setDate(lastMonday.getDate() + 21)
        // if (
        //   day.date.getTime() >= firstMonday.getTime() &&
        //   day.date.getTime() < lastMonday.getTime()
        // ) {
        //   if (day.label !== '') day.isMonday = true
        // }
      }
      // determine how to select
      // if Week: higlight the week
      // else: highlight the day
      day.isSelected = false
      if (this.selectedDay !== null) {
        if (this.config.isWeekSelect) {
          if (this.selectedWeek !== null) {
            if (
              day.date.getTime() >= this.selectedWeek.monday.getTime() &&
              day.date.getTime() <= this.selectedWeek.sunday.getTime()
            ) {
              if (day.label !== '') day.isSelected = true
            }
          }
        } else {
          day.isSelected = day.date === this.selectedDay.date
        }
      }
    },
    clickDay(day) {
      this.$emit('selectedDay', day)
      this.selectDay(day)
    },
    selectDay(day) {
      if (day.label !== '') {
        this.selectedDay = day
        this.selectedWeek = {
          monday: new Date(),
          sunday: new Date(),
        }
        this.selectedWeek.monday = new Date(
          this.getMonday(this.selectedDay.date)
        )
        this.selectedWeek.sunday = new Date(
          this.getMonday(this.selectedDay.date)
        )
        this.selectedWeek.sunday.setDate(this.selectedWeek.sunday.getDate() + 6)
        this.$emit('update:modelValue', day.isSelected ? null : day.date)
        if (this.config.isWeekSelect) {
          this.selectWeek(day)
        }
      }
    },
    enterDay(day) {},
    leaveDay(day) {},
    selectWeek(day) {
      this.getWeek(day.date)
    },
    moveThisMonth() {
      // console.log('Move This Month', {
      //   month: this.month,
      //   year: this.year,
      //   compsMonth: this.todayComps.month,
      //   compsYear: this.todayComps.year,
      // })
      this.month = this.todayComps.month
      this.year = this.todayComps.year
    },
    moveNextMonth() {
      const { month, year } = this.nextMonthComps
      this.month = month
      this.year = year
    },
    movePreviousMonth() {
      const { month, year } = this.previousMonthComps
      this.month = month
      this.year = year
    },
    moveNextYear() {
      this.year++
    },
    movePreviousYear() {
      this.year--
    },
    getMonday(d) {
      if (typeof d === 'string' || d instanceof String) d = new Date(d)
      d.setHours(0, 0, 0, 0)
      var day = d.getDay(),
        diff = d.getDate() - day + (day === 0 ? -6 : 1) // adjust when day is sunday
      return new Date(d.setDate(diff))
    },
    // isEditableMonday(d) {
    //   let editable = false
    //   d.setHours(0, 0, 0, 0)
    //   for (var i = 0; i < this.editableMondays.length; i++) {
    //     if (this.editableMondays[i].getTime() === d.getTime()) {
    //       editable = true
    //       break
    //     }
    //   }
    //   return editable
    // },
    getWeek(date) {
      // try {
      // determine the selected date's Week string
      let selectMonday = this.getMonday(date)
      let selectSunday = new Date(selectMonday)
      selectSunday.setDate(selectSunday.getDate() + 6)
      let week = `${selectMonday.getMonth() + 1}/${selectMonday.getDate()} - ${
        selectSunday.getMonth() + 1
      }/${selectSunday.getDate()}`
      // now, determine whether the date is within the
      // active Mondays
      let isSelect = false
      let thisMonday = this.getMonday(this.today)
      let firstMonday = new Date(thisMonday)
      firstMonday.setDate(firstMonday.getDate() - 14)
      let lastMonday = new Date(thisMonday)
      lastMonday.setDate(lastMonday.getDate() + 21)
      if (
        date.getTime() >= firstMonday.getTime() &&
        date.getTime() < lastMonday.getTime()
      ) {
        isSelect = true
      }
      this.$emit('selectedWeek', {
        text: week,
        monday: selectMonday,
        sunday: selectSunday,
        isSelect: isSelect,
      })
      // } catch (e) {
      //   console.log(`Error: ${e.message} (calendar.getWeek)`)
      // }
    },
  },
}
</script>
<style lang="scss" scoped>
.is_selected {
  color: #fafafa;
  background-color: #326800;
}
.is_active_monday {
  border: solid 1px rgb(157, 206, 157);
  border-radius: 5%;
}
</style>
